import { PlanExtraResponse } from '@dtx-company/true-common/src/types/Plan'

export const enum PlanCostSummaryType {
  /**
   * Used when going up a level (pro -> pro plus) or up an interval (monthly -> yearly)
   */
  UPGRADE = 'UPGRADE',
  /**
   * Used when going down a level (pro plus -> pro) or down an interval (yearly -> monthly)
   * NOTE: downgrades get queued to take effect after the current billing cycle ends
   */
  DOWNGRADE = 'DOWNGRADE',
  /**
   * Used when going from a free/basic plan to a paid one
   */
  NEW = 'NEW',
  /**
   * Used when looking at the same plan the user is already on
   */
  SAME = 'SAME',
  /**
   * Used for planExtras
   */
  EXTRAS = 'EXTRAS',
  /**
   * The plan is on a reverse free trial and can be activated.
   */
  ACTIVATE = 'ACTIVATE',
  /**
   * The plan is a switch of billing interval
   */
  SWITCH = 'SWITCH'
}

/**
 * A plan cost summary is a full summary of all line items that would
 * appear during checkout to detail the cost of upgrading/downgrading
 * the user's plan.
 *
 * The math is as follows:
 * base price
 * - discountForRemainingTimeOnNewPlan
 * = costRemainingForNewPlan
 * - couponDiscount
 * - prorationApplied
 * = subtotal
 * + tax
 * - creditBalance
 * = grandTotal
 *
 * When using this, the UI should also keep a running total to show
 * further credits or fees to ensure that the math always adds up in
 * case the BE adds additional data points in the future.
 */
export interface PlanCostSummary {
  /**
   * Additional extras that the user is purchasing, above the included amount.
   */
  additionalPlanExtras: PlanExtraResponse[]
  /**
   * When buying an annual plan, this is the discount compared to 12 months
   * of the monthly plan's price.
   *
   * This discount should come before the `basePriceInCents`.
   */
  annualDiscountInCents: number | undefined
  /**
   * The extras that are included with the plan.
   * These should all be free using our new graduated pricing plans but
   * will have a cost when using our old plans that had a fixed price.
   */
  basePlanExtras: PlanExtraResponse[]
  /**
   * The base cost of the plan by itself.
   */
  basePriceInCents: number
  /**
   * When using a coupon, this is the raw discount amount of the price.
   *
   * When upgrading, this amount is off the cost remaining for new
   * plan, otherwise it's generally off the base price.
   */
  couponDiscountInCents: number | undefined
  /**
   * If someone upgrades to a cheaper plan (like pro annual to pro plus
   * monthly) or if customer service gives them a manual adjustment
   * to their plan price then they can end up having credits in
   * their account.
   *
   * The applied credits are taken from their full balance as the
   * amount that will be applied to the cost of their plan, and
   * may result in reducing the final price to 0.00 if they have
   * enough credits to do so. Credits are applied at the very end,
   * after tax.
   */
  creditAppliedInCents: number | undefined
  /**
   * Downgrades occur once the current plan's billing cycle has
   * finished, this is the date at which that will occur.
   */
  downgradePeriodEndDate: Date | undefined
  /**
   * This is the grand total that will be charged against the
   * user's payment method.
   */
  grandTotalInCents: number
  /**
   * When upgrading, the proration is the amount of money they already paid on
   * their current plan for the days that now won't be used.
   *
   * The other side of this is the discount for remaining time on new plan.
   *
   * Ex: if they bought their plan 10 days ago and have 20 days left on
   * their billing cycle, their proration will be the cost of 20 days on
   * their current plan.
   */
  prorationAppliedInCents: number | undefined
  /**
   * The subtotal is the cost of the plan after any adjustments
   * and before tax is applied.
   */
  subtotalInCents: number
  /**
   * Tax is an additional cost based on a percentage of the subtotal.
   * It's generally based on the user's zip but BE could use
   * multiple factors to determine the tax amount.
   *
   * If undefined, it means the summary endpoint couldn't determine
   * tax based on the info it was given.
   *
   * If 0, it means the user won't be charged any tax.
   */
  taxInCents: number | undefined
  type: PlanCostSummaryType
}
